// src/store.js
import {
  configureStore,
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { axiosInstance } from "services/axios-supabase";
import { supabase } from "services/supabase";
import _ from "lodash";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  loading: false,
  error: null,
  preferences: null,
  loaded: false,
  isInitialized: false, // Track if we've attempted to load preferences at least once
  setupStep: 0, // Local state for tracking onboarding progress (synced from DB)
};

// ------------------------------------------------------------------------

export const getSupabasePreferences = createAsyncThunk(
  "data/getSupabasePreferences",
  async (userId, { rejectWithValue, getState }) => {
    // Prevent duplicate calls if already loading
    const state = getState();
    if (state.preferenceReducer.preferences) {
      return state.preferenceReducer.preferences;
    }
    try {
      let { data, error } = await supabase
        .from("preferences")
        .select("*")
        .eq("uid", userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      // If no preferences exist, create default ones
      if (!data) {
        const defaultPreferences = {
          user_id: userId,
          name: null,
          setupStatus: "not_started",
          setupStep: 0,
          utilityRate: null,
          annualSalary: null,
          defaultClientType: null,
          defaultBillRate: null,
          defaultBillable: "yes",
          debugMode: 0,
          // Invoice settings
          companyName: null,
          companyAddress: null,
          companyPhone: null,
          companyEmail: null,
          companyWebsite: null,
          invoiceInstructions: null,
          paymentPlatform: null,
          paymentLink: null,
          termsAndConditions: null,
          invoiceFooter: null,
          // Background settings
          backgroundType: "image",
          backgroundValue: "lighthouse_florida",
        };

        const { data: newData, error: insertError } = await supabase
          .from("preferences")
          .insert(defaultPreferences)
          .select()
          .single();

        if (insertError) {
          throw insertError;
        }

        data = newData;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

// Public version for fetching preferences without authentication
export const getPublicSupabasePreferences = createAsyncThunk(
  "data/getPublicSupabasePreferences",
  async (_, { rejectWithValue }) => {
    try {
      let { data, error } = await supabase
        .from("preferences")
        .select("*")
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data || {};
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const updateSupabasePreferences = createAsyncThunk(
  "data/updateSupabasePreferences",
  async (data, { rejectWithValue }) => {
    try {
      // First check if preferences exist
      let { data: existingData, error: checkError } = await supabase
        .from("preferences")
        .select("*")
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      let updatedData;
      if (existingData) {
        // Update existing record
        const { data: updateResult, error } = await supabase
          .from("preferences")
          .update(data)
          .eq("id", existingData.id)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = updateResult;
      } else {
        // Create new record
        const { data: insertResult, error } = await supabase
          .from("preferences")
          .insert(data)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = insertResult;
      }

      return updatedData;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const setSupabaseSetupStatus = createAsyncThunk(
  "data/setSupabaseSetupStatus",
  async ({ userId, setupStatus }, { rejectWithValue }) => {
    try {
      // First check if preferences exist
      let { data: existingData, error: checkError } = await supabase
        .from("preferences")
        .select("*")
        .eq("uid", userId)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      let updatedData;
      if (existingData) {
        // Update existing record
        // If setting status to complete, also set step to 5 (final step)
        const updateData =
          setupStatus === "complete"
            ? { setupStatus, setupStep: 5 }
            : { setupStatus };
        const { data: updateResult, error } = await supabase
          .from("preferences")
          .update(updateData)
          .eq("id", existingData.id)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = updateResult;
      } else {
        // Create new record with default values
        const defaultPreferences = {
          uid: userId,
          setupStatus,
          setupStep: 0,
          name: null,
          utilityRate: null,
          annualSalary: null,
          defaultClientType: null,
          defaultBillRate: null,
          defaultBillable: "yes",
          debugMode: 0,
          // Invoice settings
          companyName: null,
          companyAddress: null,
          companyPhone: null,
          companyEmail: null,
          companyWebsite: null,
          invoiceInstructions: null,
          paymentPlatform: null,
          paymentLink: null,
          termsAndConditions: null,
          invoiceFooter: null,
          // Background settings
          backgroundType: "image",
          backgroundValue: "lighthouse_florida",
        };

        const { data: insertResult, error } = await supabase
          .from("preferences")
          .insert(defaultPreferences)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = insertResult;
      }

      return updatedData;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

export const updateSetupStep = createAsyncThunk(
  "data/updateSetupStep",
  async ({ userId, step }, { rejectWithValue }) => {
    try {
      // First check if preferences exist
      let { data: existingData, error: checkError } = await supabase
        .from("preferences")
        .select("*")
        .eq("uid", userId)
        .maybeSingle();

      if (checkError) {
        throw checkError;
      }

      let updatedData;
      if (existingData) {
        // Update existing record
        const { data: updateResult, error } = await supabase
          .from("preferences")
          .update({ setupStep: step })
          .eq("id", existingData.id)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = updateResult;
      } else {
        // Create new record with default values
        const defaultPreferences = {
          uid: userId,
          setupStatus: "in_progress",
          setupStep: step,
          name: null,
          utilityRate: null,
          annualSalary: null,
          defaultClientType: null,
          defaultBillRate: null,
          defaultBillable: "yes",
          debugMode: 0,
          // Invoice settings
          companyName: null,
          companyAddress: null,
          companyPhone: null,
          companyEmail: null,
          companyWebsite: null,
          invoiceInstructions: null,
          paymentPlatform: null,
          paymentLink: null,
          termsAndConditions: null,
          invoiceFooter: null,
          // Background settings
          backgroundType: "image",
          backgroundValue: "lighthouse_florida",
        };

        const { data: insertResult, error } = await supabase
          .from("preferences")
          .insert(defaultPreferences)
          .select()
          .single();

        if (error) {
          throw error;
        }
        updatedData = insertResult;
      }

      return updatedData;
    } catch (error) {
      return rejectWithValue(error.message || error.toString());
    }
  }
);

// ------------------------------------------------------------------------
// Slices

export const PreferenceSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSetupStep: (state, action) => {
      state.setupStep = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupabasePreferences.pending, (state) => {
        state.loading = true;
        state.isInitialized = true;
      })
      .addCase(getSupabasePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        // Sync setupStep from database, default to 0 if not set
        state.setupStep = action.payload?.setupStep ?? 0;
        state.loading = false;
        state.loaded = true;
        state.isInitialized = true;
      })
      .addCase(getSupabasePreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isInitialized = true;
      })
      .addCase(getPublicSupabasePreferences.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPublicSupabasePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.loading = false;
        state.loaded = true;
      })
      .addCase(getPublicSupabasePreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSupabasePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.loaded = true;
      })
      .addCase(updateSupabasePreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loaded = false;
      })
      .addCase(setSupabaseSetupStatus.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.setupStep = action.payload?.setupStep ?? state.setupStep;
        state.loaded = true;
      })
      .addCase(updateSetupStep.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.setupStep = action.payload.setupStep;
        state.loaded = true;
      })
      .addCase(updateSetupStep.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export actions and reducers
export const { setLoading, setError, setSetupStep } = PreferenceSlice.actions;

// Selectors
export const selectPreferences = (state) => state.preferenceReducer.preferences;
export const selectPreferencesLoading = (state) =>
  state.preferenceReducer.loading;
export const selectPreferencesLoaded = (state) =>
  state.preferenceReducer.loaded;
export const selectPreferencesInitialized = (state) =>
  state.preferenceReducer.isInitialized;
export const selectPreferencesError = (state) => state.preferenceReducer.error;
export const selectSetupStep = (state) => state.preferenceReducer.setupStep;

// ------------------------------------------------------------------------
// Configure store

export default PreferenceSlice.reducer;
